import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { Mic, MicOff, Loader2, CheckCircle2, AlertCircle, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const VoiceModal = ({ open, onClose, onComplete }) => {
  const [state, setState] = useState('idle'); // idle, recording, processing, confirming, error
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editedEntry, setEditedEntry] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [prediction, setPrediction] = useState(null);
  const [showPrediction, setShowPrediction] = useState(true);
  
  // Time range picker state
  const [useTimeRange, setUseTimeRange] = useState(false);
  const [startHour, setStartHour] = useState('09');
  const [startMinute, setStartMinute] = useState('00');
  const [endHour, setEndHour] = useState('17');
  const [endMinute, setEndMinute] = useState('00');
  const [durationHours, setDurationHours] = useState(1);
  const [durationMinutes, setDurationMinutes] = useState(0);

  // Generate time options
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  const getLocalDateInputValue = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (value) => {
    if (!value) return;
    const [year, month, day] = value.split('-').map(Number);
    const updated = new Date(year, month - 1, day, 0, 0, 0, 0);
    setEditedEntry((prev) => ({
      ...prev,
      date: updated.toISOString()
    }));
  };

  const updateDurationFromParts = (hoursPart, minutesPart) => {
    const safeHours = Math.max(0, Number.isNaN(hoursPart) ? 0 : hoursPart);
    let safeMinutes = Math.max(0, Number.isNaN(minutesPart) ? 0 : minutesPart);

    if (safeMinutes >= 60) {
      const extraHours = Math.floor(safeMinutes / 60);
      safeMinutes = safeMinutes % 60;
      return updateDurationFromParts(safeHours + extraHours, safeMinutes);
    }

    setDurationHours(safeHours);
    setDurationMinutes(safeMinutes);
    setEditedEntry((prev) => ({
      ...prev,
      duration_minutes: safeHours * 60 + safeMinutes
    }));
    return null;
  };

  const handleHoursChange = (value) => {
    const numeric = parseInt(value, 10);
    updateDurationFromParts(Number.isNaN(numeric) ? 0 : numeric, durationMinutes);
  };

  const handleMinutesChange = (value) => {
    const numeric = parseInt(value, 10);
    const safe = Number.isNaN(numeric) ? 0 : numeric;
    if (safe >= 60) {
      updateDurationFromParts(durationHours + Math.floor(safe / 60), safe % 60);
    } else {
      updateDurationFromParts(durationHours, safe);
    }
  };
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const fullTranscriptRef = useRef('');

  useEffect(() => {
    if (open) {
      fetchProjects();
      resetState();
    }
  }, [open]);

  useEffect(() => {
    if (!editedEntry || useTimeRange) return;
    const total = Number(editedEntry.duration_minutes);
    if (Number.isNaN(total) || total < 0) {
      setDurationHours(0);
      setDurationMinutes(0);
      return;
    }
    const hrs = Math.floor(total / 60);
    const mins = total % 60;
    if (hrs !== durationHours || mins !== durationMinutes) {
      setDurationHours(hrs);
      setDurationMinutes(mins);
    }
  }, [editedEntry?.duration_minutes, useTimeRange, durationHours, durationMinutes]);

  useEffect(() => {
    if (state === 'recording') {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setRecordingTime(0);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [state]);

  const resetState = () => {
    setState('idle');
    setAudioBlob(null);
    setTranscription('');
    setParsedData(null);
    setEditedEntry(null);
    setRecordingTime(0);
    setPrediction(null);
    setShowPrediction(true);
    setUseTimeRange(false);
    setStartHour('09');
    setStartMinute('00');
    setEndHour('17');
    setEndMinute('00');
    setDurationHours(1);
    setDurationMinutes(0);
    chunksRef.current = [];
  };

  // Calculate duration from time range
  const calculateDurationFromTimeRange = () => {
    const startTotalMinutes = parseInt(startHour) * 60 + parseInt(startMinute);
    const endTotalMinutes = parseInt(endHour) * 60 + parseInt(endMinute);
    
    return endTotalMinutes - startTotalMinutes;
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to create MediaRecorder with different mime types
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg';
        } else {
          mimeType = '';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      fullTranscriptRef.current = '';

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setAudioBlob(blob);
        processAudio(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Stop speech recognition
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };

      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        toast.error('Recording error occurred');
        setState('error');
        stream.getTracks().forEach(track => track.stop());
        
        // Stop speech recognition
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };

      // Start Web Speech API for voice command detection
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        
        recognition.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }
          
          // Build full transcript
          if (finalTranscript) {
            fullTranscriptRef.current += finalTranscript;
          }
          
          // Check for stop command in interim or final results
          const currentText = (fullTranscriptRef.current + interimTranscript).toLowerCase();
          if (currentText.includes('stop recording')) {
            console.log('Stop recording command detected!');
            stopRecording();
          }
        };
        
        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          // Don't show error for common issues like no-speech
          if (event.error !== 'no-speech' && event.error !== 'aborted') {
            console.warn('Speech recognition error:', event.error);
          }
        };
        
        recognitionRef.current = recognition;
        recognition.start();
      }

      mediaRecorder.start();
      setState('recording');
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error(`Microphone error: ${error.message}`);
      setState('error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    // Stop speech recognition
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  };

  const processAudio = async (blob) => {
    setState('processing');
    
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');

      console.log('Uploading audio, size:', blob.size, 'type:', blob.type);

      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout
      });

      console.log('Transcription response:', response.data);

      setTranscription(response.data.transcription_text);
      setParsedData(response.data.parsed_entry);
      
      // Initialize edited entry with parsed data
      setEditedEntry({
        project_id: null,
        date: new Date().toISOString(),
        duration_minutes: response.data.parsed_entry.duration_minutes,
        entry_type: response.data.parsed_entry.entry_type,
        notes: response.data.parsed_entry.notes,
        attendees: response.data.parsed_entry.attendees,
        raw_voice_input: response.data.transcription_text
      });

      // Try to match project name
      let initialProjectId = null;
      if (response.data.parsed_entry.project_name) {
        const matchedProject = projects.find(p => 
          p.name.toLowerCase().includes(response.data.parsed_entry.project_name.toLowerCase())
        );
        if (matchedProject) {
          initialProjectId = matchedProject.id;
        }
      }
      
      // Get AI prediction
      try {
        const predictionResponse = await axios.post(`${API}/projects/predict`, {
          description: response.data.parsed_entry.notes,
          attendees: response.data.parsed_entry.attendees,
          date: new Date().toISOString(),
          time: new Date().toISOString()
        });
        
        if (predictionResponse.data) {
          setPrediction(predictionResponse.data);
          
          // If no project matched from voice and we have high confidence prediction, use it
          if (!initialProjectId && predictionResponse.data.confidence >= 0.75) {
            initialProjectId = predictionResponse.data.project_id;
            setEditedEntry(prev => ({ 
              ...prev, 
              project_id: initialProjectId,
              prediction_used: true,
              prediction_confidence: predictionResponse.data.confidence
            }));
          } else if (initialProjectId) {
            setEditedEntry(prev => ({ ...prev, project_id: initialProjectId }));
          }
        }
      } catch (predError) {
        console.log('No prediction available:', predError);
        if (initialProjectId) {
          setEditedEntry(prev => ({ ...prev, project_id: initialProjectId }));
        }
      }

      setState('confirming');
    } catch (error) {
      console.error('Failed to process audio:', error);
      let errorMessage = 'Failed to process audio';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setState('error');
    }
  };

  const saveEntry = async () => {
    try {
      // Validate time range if used
      if (useTimeRange) {
        const duration = calculateDurationFromTimeRange();
        if (duration <= 0) {
          toast.error('End time must be after start time');
          return;
        }
      }

      // Calculate final duration
      let finalDuration = editedEntry.duration_minutes;
      let finalDate = editedEntry.date;

      if (!useTimeRange) {
        if (!finalDuration || finalDuration <= 0) {
          toast.error('Duration must be greater than 0');
          return;
        }
      }
      
      if (useTimeRange) {
        finalDuration = calculateDurationFromTimeRange();
        
        // Create date with start time
        const [year, month, day] = editedEntry.date.split('T')[0].split('-').map(Number);
        const hours = parseInt(startHour);
        const mins = parseInt(startMinute);
        const dateWithTime = new Date(year, month - 1, day, hours, mins, 0, 0);
        finalDate = dateWithTime.toISOString();
        
        // Calculate start_hour and end_hour
        const endHours = parseInt(endHour);
        
        // Add prediction tracking and ensure date is properly formatted
        const entryToSave = {
          ...editedEntry,
          date: finalDate,
          duration_minutes: finalDuration,
          start_hour: hours,
          end_hour: endHours,
          prediction_used: prediction && editedEntry.project_id === prediction.project_id,
          prediction_confidence: prediction?.confidence || null
        };
        
        console.log('Saving entry with time range:', entryToSave);
        await axios.post(`${API}/time-entries`, entryToSave);
      } else {
        // Standard duration-based entry
        const entryToSave = {
          ...editedEntry,
          date: finalDate,
          duration_minutes: finalDuration,
          prediction_used: prediction && editedEntry.project_id === prediction.project_id,
          prediction_confidence: prediction?.confidence || null
        };
        
        console.log('Saving entry:', entryToSave);
        await axios.post(`${API}/time-entries`, entryToSave);
      }
      
      toast.success('Time entry saved!');
      onComplete();
      onClose();
    } catch (error) {
      console.error('Failed to save entry:', error);
      console.error('Error details:', error.response?.data);
      let errorMessage = 'Failed to save time entry';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      
      {/* Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal Panel */}
        <Dialog.Panel 
          className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[95vw] md:max-w-[500px] max-h-[85vh] md:max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors" 
          data-testid="voice-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10 transition-colors">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors">
                Log Time with Voice
              </Dialog.Title>
              <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">
                {state === 'idle' && 'Tap the microphone to start recording'}
                {state === 'recording' && 'Speak your time entry...'}
                {state === 'processing' && 'Processing your recording...'}
                {state === 'confirming' && 'Review and confirm your entry'}
                {state === 'error' && 'Something went wrong'}
              </Dialog.Description>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 bg-white dark:bg-gray-800 transition-colors">
          {/* Recording State */}
          {state === 'recording' && (
            <div className="text-center" data-testid="recording-state">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
                <div className="relative w-32 h-32 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl">
                  <Mic className="w-16 h-16 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2 transition-colors" data-testid="recording-time">{formatTime(recordingTime)}</p>
              <p className="text-gray-600 dark:text-gray-400 mb-2 transition-colors">Listening...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 transition-colors">Say "Stop recording" to finish</p>
              <div className="flex justify-center space-x-4">
                <Button
                  onClick={stopRecording}
                  size="lg"
                  className="bg-red-500 hover:bg-red-600"
                  data-testid="stop-recording-button"
                >
                  Stop Recording
                </Button>
                <Button
                  onClick={() => {
                    stopRecording();
                    resetState();
                  }}
                  variant="outline"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Processing State */}
          {state === 'processing' && (
            <div className="text-center py-12" data-testid="processing-state">
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-cyan-500 dark:text-cyan-400 animate-spin transition-colors" />
              <p className="text-lg text-gray-600 dark:text-gray-400 transition-colors">Processing your entry...</p>
            </div>
          )}

          {/* Confirming State */}
          {state === 'confirming' && editedEntry && (
            <div className="space-y-4" data-testid="confirm-state">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 transition-colors">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 transition-colors">Original transcription:</p>
                <p className="text-gray-800 dark:text-gray-200 transition-colors" data-testid="transcription-text">{transcription}</p>
              </div>

              {/* AI Prediction Banner */}
              {prediction && showPrediction && (
                <div className={`rounded-lg p-4 border-2 transition-colors ${
                  prediction.confidence >= 0.8 ? 'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-600' :
                  prediction.confidence >= 0.6 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500 dark:border-yellow-600' :
                  'bg-gray-50 dark:bg-gray-700/50 border-gray-400 dark:border-gray-600'
                }`} data-testid="prediction-banner">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{prediction.confidence >= 0.7 ? '🎯' : '💡'}</span>
                      <div>
                        <h3 className="font-semibold text-gray-800 dark:text-gray-200 transition-colors">
                          {prediction.confidence >= 0.7 ? 'Suggested Project (High Confidence)' : 'Suggested Project'}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">{prediction.reason}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowPrediction(false)}
                      className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-lg" style={{ color: prediction.project_color }}>
                        {prediction.project_name}
                      </span>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 transition-colors">
                        {Math.round(prediction.confidence * 100)}% confident
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden transition-colors">
                      <div 
                        className={`h-full ${
                          prediction.confidence >= 0.8 ? 'bg-green-500' :
                          prediction.confidence >= 0.6 ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`}
                        style={{ width: `${prediction.confidence * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  {editedEntry.project_id !== prediction.project_id && (
                    <button
                      onClick={() => {
                        setEditedEntry(prev => ({ 
                          ...prev, 
                          project_id: prediction.project_id,
                          prediction_used: true,
                          prediction_confidence: prediction.confidence
                        }));
                        setShowPrediction(false);
                      }}
                      className="w-full py-2 px-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-md font-medium hover:from-purple-600 hover:to-purple-700 transition-all"
                    >
                      Use This Project
                    </button>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="voice-entry-date" className="text-gray-700 dark:text-gray-300 transition-colors">Date</Label>
                  <Input
                    id="voice-entry-date"
                    type="date"
                    value={getLocalDateInputValue(editedEntry.date)}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                  />
                </div>

                <div>
                  <Label htmlFor="project" className="text-gray-700 dark:text-gray-300 transition-colors">Project</Label>
                  <select
                    id="project"
                    value={editedEntry.project_id || 'none'}
                    onChange={(e) => setEditedEntry({ ...editedEntry, project_id: e.target.value === 'none' ? null : e.target.value })}
                    className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
                    data-testid="project-select"
                  >
                    <option value="none">No Project</option>
                    {projects.map(proj => (
                      <option key={proj.id} value={proj.id}>{proj.name}</option>
                    ))}
                  </select>
                </div>

                {/* Duration Mode Toggle */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-700 dark:text-gray-300 transition-colors">
                      {useTimeRange ? 'Time Range' : 'Duration'}
                    </Label>
                    <button
                      type="button"
                      onClick={() => setUseTimeRange(!useTimeRange)}
                      className="text-xs text-purple-600 dark:text-purple-400 underline bg-transparent border-none cursor-pointer hover:text-purple-700 dark:hover:text-purple-300 transition-colors"
                    >
                      {useTimeRange ? 'Use duration' : 'Use time range'}
                    </button>
                  </div>

                  {useTimeRange ? (
                    <div>
                      {/* Start Time Dropdowns */}
                      <div className="mb-3">
                        <Label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 transition-colors">
                          Start Time
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={startHour}
                            onChange={(e) => setStartHour(e.target.value)}
                            className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm cursor-pointer transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400"
                          >
                            {hours.map(h => (
                              <option key={h} value={h}>
                                {h}
                              </option>
                            ))}
                          </select>
                          <select
                            value={startMinute}
                            onChange={(e) => setStartMinute(e.target.value)}
                            className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm cursor-pointer transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400"
                          >
                            {minutes.map(m => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* End Time Dropdowns */}
                      <div className="mb-3">
                        <Label className="block text-xs text-gray-600 dark:text-gray-400 mb-1.5 transition-colors">
                          End Time
                        </Label>
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={endHour}
                            onChange={(e) => setEndHour(e.target.value)}
                            className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm cursor-pointer transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400"
                          >
                            {hours.map(h => (
                              <option key={h} value={h}>
                                {h}
                              </option>
                            ))}
                          </select>
                          <select
                            value={endMinute}
                            onChange={(e) => setEndMinute(e.target.value)}
                            className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md text-sm cursor-pointer transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 focus:border-cyan-500 dark:focus:border-cyan-400"
                          >
                            {minutes.map(m => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Duration Display */}
                      <div className="p-3 bg-gray-100 dark:bg-gray-700/50 rounded-md text-center border border-gray-200 dark:border-gray-600 transition-colors">
                        <p className="text-sm text-gray-700 dark:text-gray-300 font-medium transition-colors">
                          Duration: {Math.floor(calculateDurationFromTimeRange() / 60)}h {calculateDurationFromTimeRange() % 60}m
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Label className="text-gray-700 dark:text-gray-300 transition-colors">Duration</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1 transition-colors">Hours</span>
                          <Input
                            type="number"
                            min="0"
                            value={durationHours}
                            onChange={(e) => handleHoursChange(e.target.value)}
                            className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                            data-testid="duration-hours-input"
                          />
                        </div>
                        <div>
                          <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1 transition-colors">Minutes</span>
                          <Input
                            type="number"
                            min="0"
                            max="59"
                            value={durationMinutes}
                            onChange={(e) => handleMinutesChange(e.target.value)}
                            className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                            data-testid="duration-minutes-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
                        Total: {durationHours}h {durationMinutes}m
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="entry-type">Entry Type</Label>
                  <select
                    id="entry-type"
                    value={editedEntry.entry_type}
                    onChange={(e) => setEditedEntry({ ...editedEntry, entry_type: e.target.value })}
                    className="flex h-11 w-full rounded-md border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:focus-visible:ring-cyan-400 focus-visible:border-cyan-500 dark:focus-visible:border-cyan-400 focus-visible:ring-offset-2 transition-colors font-medium"
                    data-testid="entry-type-select"
                  >
                    <option value="work">Work</option>
                    <option value="meeting">Meeting</option>
                    <option value="break">Break</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="notes" className="text-gray-700 dark:text-gray-300 transition-colors">Notes</Label>
                  <Textarea
                    id="notes"
                    value={editedEntry.notes}
                    onChange={(e) => setEditedEntry({ ...editedEntry, notes: e.target.value })}
                    rows={3}
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                    data-testid="notes-input"
                  />
                </div>

                {editedEntry.attendees && editedEntry.attendees.length > 0 && (
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300 transition-colors">Attendees</Label>
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="attendees-list">
                      {editedEntry.attendees.map((attendee, index) => (
                        <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm border border-purple-200 dark:border-purple-800 transition-colors">
                          {attendee}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                <Button
                  onClick={saveEntry}
                  className="w-full sm:flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                  data-testid="save-entry-button"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Save Entry
                </Button>
                <Button
                  onClick={resetState}
                  variant="outline"
                  className="w-full sm:w-auto border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  data-testid="try-again-button"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {/* Error State */}
          {state === 'error' && (
            <div className="text-center py-8" data-testid="error-state">
              <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500 dark:text-red-400 transition-colors" />
              <p className="text-lg text-gray-800 dark:text-gray-200 mb-6 transition-colors">Couldn't process audio</p>
              <div className="flex justify-center space-x-3">
                <Button onClick={resetState}>Try Again</Button>
                <Button variant="outline" onClick={onClose} className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</Button>
              </div>
            </div>
          )}

          {/* Idle State */}
          {state === 'idle' && (
            <div className="text-center py-8" data-testid="idle-state">
              <button
                onClick={startRecording}
                className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-cyan-400 to-teal-500 shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-300 mb-6"
                data-testid="start-recording-button"
              >
                <Mic className="w-16 h-16 text-white" />
              </button>
              <p className="text-lg text-gray-600 dark:text-gray-400 transition-colors">Tap to start recording</p>
            </div>
          )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default VoiceModal;
